import { useEffect, useMemo, useState } from 'react'
import { SmartBack } from '../navigation/SmartBack.js'
import {
  ArrowLeftRight, Activity, BarChart3, CalendarDays, Clock, Coins, DoorOpen, GlassWater, LineChart,
  MapPin, Radio, Receipt, Tag, Ticket, TrendingUp, Trophy, UserCheck, Users,
} from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { Link } from '../router.js'
import { Drawer } from '../admin/primitives.js'
import { registerPageLayout } from '../layout/registry.js'
import { LayoutEditControls, LayoutGrid, useLayoutState } from '../layout/LayoutGrid.js'
import { SoireeDataProvider, useSoiree } from './SoireeData.js'
import {
  AnalyticsExplorerWidget, ChannelsWidget, ComparisonWidget, ForecastCurrentWidget,
  ForecastHistoryWidget, ForecastVsActualWidget, MiniAnalytics, OperationsWidget,
  ProductsWidget, ScanInsightsBlock, TicketsOverviewWidget, RoomsWidget,
} from './widgets.js'
import { OperationalTimelineWidget } from './OperationalTimeline.js'
import { PhasePill, SOURCE_LABELS, jourFrAnnee, plageHoraire } from './lib.js'
import { NightContextWidget } from './SoireeNightWidgets.js'
import { SoireeFinanceWidget } from './SoireeFinanceWidget.js'
import { SoireePersonnelWidget } from './SoireePersonnelWidget.js'
import { CaisseSoireeContexte, SoireeCaisseWidget, useCaisseNuit } from './CaisseNuit.js'
import { tuileCaisseAbsente } from './caisse-nuit-model.js'
import { tauxFr, tuileDeLaFiche, tuilesAbsentesPourSource, ventesParBilletterie } from './nuit-billetteries-model.js'
import { BilletteriesDeLaSoiree, NomsAssociations } from './Rattachements.js'
import { FormulesBarWidget, VenueAssociationsWidget } from './Formules.js'
import { tuilesFormulesAbsentes } from './formules-model.js'
import { deNom, prefixeSoireeDe } from './rattachement-model.js'
import { ComparerPrecedente } from './ComparerPrecedente.js'
import { BoutonExportCsv } from '../admin/BoutonExportCsv.js'
import { urlExportCsvSoiree } from '../admin/telechargement.js'

/**
 * /soirees/:id — LE COCKPIT DE LA NUIT (v2).
 *
 * Page personnalisable via le Page Layout Engine (première page activée) :
 * chaque bloc est un widget (drag/resize/masquage, disposition personnelle,
 * Save Bar). Les drill-downs respectent le RBAC des domaines sources.
 */

registerPageLayout({
  pageKey: 'soiree-detail',
  widgets: [
    // La programmation vit dans SALLES — un seul concept, une seule section.
    { id: 'night-context', Icon: MapPin, label: 'Autour de Rennes — cette soirée', description: 'Concurrence pendant cette nuit précise', minW: 4, minH: 4, defaultW: 8, defaultH: 6, Component: NightContextWidget },
    /* minH 3 : la carte tient son contenu en 280 px (3 rangées). Le minimum
       était à 4, si bien que toute sauvegarde de disposition ramenait la
       tuile à 378 px et rouvrait les 97 px de vide corrigés plus tôt. */
    { id: 'tickets-overview', Icon: Ticket, label: 'Billetterie', description: 'Compteur de scans et totaux', minW: 3, minH: 3, defaultW: 4, defaultH: 4, Component: TicketsOverviewWidget },
    /* La valeur faciale de la nuit — jamais un chiffre d'affaires. Le RBAC au
       rendu ne suffit pas et ne prétend pas suffire : sans `finance.live.read`
       le serveur ne transmet AUCUN montant (voir SoireeFinanceWidget). */
    { id: 'finance', Icon: Receipt, label: 'Billetterie — valeur faciale', description: 'Valeur faciale provisoire de la nuit, avec ses réserves', requiredPermission: 'finance.live.read', minW: 3, minH: 3, defaultW: 4, defaultH: 5, Component: SoireeFinanceWidget },
    { id: 'analytics', Icon: BarChart3, label: 'Analytics', description: 'Explorateur configurable', minW: 5, minH: 4, defaultW: 8, defaultH: 5, Component: AnalyticsExplorerWidget },
    { id: 'comparison', Icon: Trophy, label: 'Performance', description: 'Comparaison N-1 et records', minW: 3, minH: 3, defaultW: 4, defaultH: 4, Component: ComparisonWidget },
    { id: 'operations', Icon: Radio, label: 'Opérations', description: 'Sécurité, staff, infrastructure, annonce de fermeture', minW: 4, minH: 4, defaultW: 8, defaultH: 4, Component: OperationsWidget },
    /* Le personnel de la soirée vient du MÊME read model que l'écran RH
       Présence et que la borne : « 17 présents » veut dire la même chose
       partout, parce que c'est calculé au même endroit. */
    { id: 'personnel', Icon: Users, label: 'Personnel', description: 'Qui est réellement en poste sur cette soirée', requiredPermission: 'time.read.team', minW: 3, minH: 3, defaultW: 4, defaultH: 5, Component: SoireePersonnelWidget },
    { id: 'rooms', Icon: DoorOpen, label: 'Salles', description: 'Salles ouvertes et line-up', minW: 3, minH: 2, defaultW: 4, defaultH: 3, Component: RoomsWidget },
    { id: 'products', Icon: Tag, label: 'Tarifs', description: 'Répartition par tarif', minW: 3, minH: 3, defaultW: 4, defaultH: 4, Component: ProductsWidget },
    { id: 'channels', Icon: Activity, label: 'Canaux', description: 'Répartition par canal de vente', minW: 3, minH: 3, defaultW: 4, defaultH: 4, Component: ChannelsWidget },
    { id: 'timeline', Icon: Clock, label: 'Timeline', description: 'Le récit de la soirée', minW: 4, minH: 3, defaultW: 12, defaultH: 4, Component: OperationalTimelineWidget },
    // v4 — prévisions : fourchette actuelle (soirée à venir), vs réel et
    // historique des snapshots (soirée terminée). Masquables comme le reste.
    { id: 'forecast-current', Icon: TrendingUp, label: 'Prévision', description: 'Fourchette actuelle et facteurs (soirée à venir)', minW: 3, minH: 3, defaultW: 4, defaultH: 4, Component: ForecastCurrentWidget },
    { id: 'forecast-vs-actual', Icon: LineChart, label: 'Prévision vs réel', description: 'Dernière prévision avant ouverture face au réel', minW: 3, minH: 3, defaultW: 4, defaultH: 4, Component: ForecastVsActualWidget },
    { id: 'forecast-history', Icon: LineChart, label: 'Historique des prévisions', description: 'Évolution des snapshots J-30 → réel', minW: 4, minH: 3, defaultW: 8, defaultH: 4, Component: ForecastHistoryWidget },
    /* LA CAISSE DE LA NUIT (15/10/2026) — Rapports, Compté, CA arrêté : les
       chiffres de la fiche de nuit, en second plan. Les montants ne partent
       qu'avec les droits de la fiche (lire les clôtures ET les montants des
       sources) ; sans eux, ou l'emplacement éteint dans les Réglages, la page
       retire la tuile (`absentes`). */
    { id: 'night-cash', Icon: Coins, label: 'Caisse de la nuit', description: 'Rapports et compté de la nuit, puis son CA arrêté une fois finalisée (ou validé, pour une nuit reprise)', requiredPermission: 'finance.source.read', minW: 3, minH: 2, defaultW: 4, defaultH: 3, Component: SoireeCaisseWidget },
    /* LES BILLETS DES ASSOCIATIONS FORMULE PAR FORMULE (19/10/2026) : la
       venue (vendus face aux entrées — le no-show, la nuit finie) et les
       formules pour le bar, en direct. Retirées d'une soirée sans billet
       d'association, ou éteintes dans Réglages Soirées › Billetteries. */
    { id: 'association-venue', Icon: UserCheck, label: 'Venue des associations', description: 'Billets vendus face aux entrées, par formule et par association', minW: 3, minH: 3, defaultW: 4, defaultH: 5, Component: VenueAssociationsWidget },
    { id: 'bar-formules', Icon: GlassWater, label: 'Formules pour le bar', description: 'Essentiel, Soft, Prestige vendus et entrés, en direct', minW: 3, minH: 3, defaultW: 4, defaultH: 4, Component: FormulesBarWidget },
  ],
  defaultLayout: [
    { id: 'tickets-overview', x: 0, y: 0, w: 4, h: 3 },
    { id: 'analytics', x: 4, y: 0, w: 8, h: 5 },
    // Sous le compteur de billets : on lit d'abord combien, puis combien ça vaut.
    { id: 'finance', x: 0, y: 3, w: 4, h: 5 },
    // Placé haut : c'est une information qu'on consulte le soir même.
    { id: 'night-context', x: 0, y: 5, w: 12, h: 6 },
    { id: 'operations', x: 0, y: 5, w: 8, h: 4 },
    { id: 'comparison', x: 8, y: 5, w: 4, h: 4 },
    { id: 'rooms', x: 8, y: 9, w: 4, h: 3 },
    { id: 'products', x: 0, y: 9, w: 4, h: 4 },
    { id: 'channels', x: 4, y: 9, w: 4, h: 4 },
    { id: 'timeline', x: 0, y: 13, w: 12, h: 4 },
    { id: 'forecast-current', x: 0, y: 17, w: 4, h: 4 },
    { id: 'forecast-vs-actual', x: 4, y: 17, w: 4, h: 4 },
    { id: 'forecast-history', x: 0, y: 21, w: 12, h: 4 },
    // En bas, et seulement une fois la soirée lancée : une information de bilan, pas de pilotage.
    { id: 'night-cash', x: 0, y: 25, w: 4, h: 3, visibility: 'FROM_START' },
    // Sous les tarifs : la même lecture (vendus / entrés), pour les billets des associations.
    { id: 'bar-formules', x: 4, y: 28, w: 4, h: 4 },
    { id: 'association-venue', x: 8, y: 28, w: 4, h: 5 },
  ],
})

/* ═══════════ drawers de drill-down (RBAC respecté) ═══════════ */

function DrillDrawers() {
  const { id, drill, closeDrill, detail, projection } = useSoiree()
  const productName = drill?.startsWith('product:') ? drill.slice(8) : null
  const product = productName ? detail?.products.find((p) => p.name === productName) ?? null : null
  /* Soirée association (20/10/2026) : la tuile compte toute la nuit, ses
     tiroirs aussi — ils disaient « — » partout le 09/09 (Shotgun seul). */
  const tuile = tuileDeLaFiche(detail)
  const nuitEntiere = tuile.mode === 'NUIT' && tuile.nuit !== null

  return (
    <>
      <Drawer open={drill === 'scans'} title={nuitEntiere ? 'Contrôle d’entrée — toute la nuit' : 'Contrôle d\'entrée — analyse'} onClose={closeDrill}>
        <ScanInsightsBlock id={id} nuit={nuitEntiere} />
        <h3 className="so-month__name" style={{ marginTop: 16 }}>Scans cumulés</h3>
        {drill === 'scans' ? <MiniAnalytics id={id} metric="scans" groupBy="none" nuit={nuitEntiere} /> : null}
      </Drawer>

      <Drawer open={drill === 'sales'} title={nuitEntiere ? 'Ventes — toute la nuit' : 'Ventes — analyse'} onClose={closeDrill}>
        {nuitEntiere ? (
          <dl className="lc-kv">
            <div><dt>Billets valides (vendus), toutes billetteries</dt><dd>{tuile.vendus?.toLocaleString('fr-FR') ?? '—'}</dd></div>
            {ventesParBilletterie(tuile.nuit!).map((v) => (
              <div key={v.billetterie}><dt>Vendus sur {v.billetterie}</dt><dd>{v.valid.toLocaleString('fr-FR')}</dd></div>
            ))}
            <div><dt>Taux de scan de la nuit</dt><dd>{tauxFr(tuile.taux)}</dd></div>
          </dl>
        ) : null}
        {nuitEntiere ? <h3 className="so-month__name" style={{ marginTop: 16 }}>Billetterie du club</h3> : null}
        <dl className="lc-kv">
          <div><dt>Billets valides (vendus)</dt><dd>{detail?.soiree.stats?.tickets_valid ?? '—'}</dd></div>
          <div><dt>Billets émis</dt><dd>{detail?.soiree.stats?.tickets_issued ?? '—'}</dd></div>
          <div><dt>Invitations</dt><dd>{detail?.soiree.stats?.tickets_invitation ?? '—'}</dd></div>
          <div><dt>Remboursés</dt><dd>{detail?.soiree.stats?.tickets_refunded ?? '—'}</dd></div>
          <div><dt>Places revendues (dédupliquées)</dt><dd>{detail?.soiree.stats?.tickets_resold ?? '—'}</dd></div>
        </dl>
        <h3 className="so-month__name" style={{ marginTop: 16 }}>Ventes cumulées (fenêtre de prévente{nuitEntiere ? ', toute la nuit' : ''})</h3>
        {drill === 'sales' ? <MiniAnalytics id={id} metric="sales" groupBy="none" nuit={nuitEntiere} /> : null}
      </Drawer>

      <Drawer open={product !== null} title={productName ?? ''} onClose={closeDrill}>
        {product ? (
          <>
            <dl className="lc-kv">
              <div><dt>Billets valides</dt><dd>{product.tickets_valid}</dd></div>
              <div><dt>Billets scannés</dt><dd>{product.tickets_scanned}</dd></div>
              <div><dt>Taux de scan</dt><dd>{product.scan_rate !== null ? `${product.scan_rate.toLocaleString('fr-FR')} %` : '—'}</dd></div>
              {product.category ? <div><dt>Catégorie</dt><dd>{product.category}</dd></div> : null}
            </dl>
            <h3 className="so-month__name" style={{ marginTop: 16 }}>Scans cumulés de ce tarif</h3>
            <MiniAnalytics id={id} metric="scans" groupBy="product" only={product.name} />
          </>
        ) : null}
      </Drawer>

      <Drawer open={drill === 'security'} title="Sécurité — résumé de la nuit" onClose={closeDrill}>
        <dl className="lc-kv">
          <div><dt>Agents sur place</dt><dd>{projection?.security.agents_on_duty ?? '—'}</dd></div>
          <div><dt>Incidents actifs</dt><dd>{projection?.security.open_incidents ?? '—'}</dd></div>
          <div><dt>Incidents critiques</dt><dd>{projection?.security.critical_incidents ?? '—'}</dd></div>
          <div><dt>Entrées consignées (main courante)</dt><dd>{projection?.security.entries_total ?? '—'}</dd></div>
        </dl>
        {projection?.can_drill.security ? (
          <p style={{ marginTop: 14 }}><Link href="/security" className="lc-btn">Ouvrir le hub Sécurité</Link></p>
        ) : (
          <p className="lc-hint" style={{ marginTop: 14 }}>
            Résumé autorisé par votre accès Soirées — le détail nominatif
            relève du hub Sécurité.
          </p>
        )}
      </Drawer>

      <Drawer open={drill === 'staff'} title="Staff — présence" onClose={closeDrill}>
        <dl className="lc-kv">
          <div><dt>Présents confirmés</dt><dd>{projection?.staff.present_confirmed ?? '—'}</dd></div>
          <div><dt>À confirmer</dt><dd>{projection?.staff.uncertain ?? '—'}</dd></div>
        </dl>
        <p className="lc-hint" style={{ marginTop: 10 }}>
          « À confirmer » : pointages auto-clos par Skello à minuit — des
          personnes vraisemblablement encore présentes, jamais comptées zéro.
        </p>
        {projection?.can_drill.presence ? (
          <p style={{ marginTop: 14 }}><Link href="/equipe/temps/presences" className="lc-btn">Ouvrir la présence staff</Link></p>
        ) : null}
      </Drawer>

      <Drawer open={drill === 'infrastructure'} title="Infrastructure — résumé" onClose={closeDrill}>
        <dl className="lc-kv">
          <div><dt>Sources surveillées</dt><dd>{projection?.infrastructure.sources_total ?? '—'}</dd></div>
          <div><dt>À surveiller</dt><dd>{projection?.infrastructure.sources_attention ?? '—'}</dd></div>
          <div><dt>Alertes critiques</dt><dd>{projection?.infrastructure.critical_alerts ?? '—'}</dd></div>
        </dl>
        {projection?.can_drill.infrastructure ? (
          <p style={{ marginTop: 14 }}><Link href="/infrastructure" className="lc-btn">Ouvrir l'infrastructure</Link></p>
        ) : null}
      </Drawer>
    </>
  )
}

/* ═══════════ page ═══════════ */

function SoireeContent({ id }: { id: string }) {
  const { detail, reloadDetail } = useSoiree()
  const layout = useLayoutState('soiree-detail')
  const s = detail?.soiree ?? null
  // Une lecture pour la page : elle remplit la tuile ET décide si elle existe.
  const caisse = useCaisseNuit('soiree_page', { soiree: id })
  // Retirée quand la réponse ne nomme pas CETTE soirée — y compris celle de la soirée précédente.
  // Une soirée d'association : ni valeur faciale (hors Comptabilité), ni
  // prévision ni comparaison tant qu'elle est hors analyses.
  const source = s?.source ?? null
  const analyses = detail?.billetteries?.include_in_analytics ?? false
  // « Soirée association » : les tuiles qui ne liraient que la billetterie du club (prévisions, et la
  // comparaison quand elle est réglée sur Shotgun seul) disparaissent ; l'en-tête dit pourquoi.
  const assoc = detail?.rattachement?.soiree_association ?? null
  const assos = detail?.associations ?? null
  const assosNuit = assos?.de_la_soiree ?? []
  const formules = detail?.formules
  const affichage = detail?.billetteries
  const absentes = useMemo(() => [
    ...(tuileCaisseAbsente(caisse, id) ? ['night-cash'] : []),
    ...tuilesAbsentesPourSource(source, { include_in_analytics: analyses }),
    ...(assoc?.tuiles_masquees ?? []),
    // Avant la première lecture, rien n'est retiré : la grille ne saute pas.
    ...(detail ? tuilesFormulesAbsentes(formules, affichage) : []),
  ], [caisse, id, source, analyses, assoc, detail, formules, affichage])

  return (
    <>
      <header className="so-head">
        <div className="so-head__main">
          <h1 className="lc-title">{s?.name ?? '…'}</h1>
          {s ? (
            <p className="so-head__when">
              <CalendarDays size={15} aria-hidden="true" />
              {jourFrAnnee(s.starts_at)} · {plageHoraire(s.starts_at, s.ends_at)}
              {s.family ? <span className="so-head__family">{s.family}</span> : null}
            </p>
          ) : <p className="so-head__when lc-hint">Chargement…</p>}
          {/* « En cours » avant ses propres heures : la porte scanne déjà pour
              les associations — dit pourquoi, comme l'Accueil et la carte Sécurité. */}
          {s?.ouverte_avant ? <p className="lc-hint">{s.ouverte_avant.libelle}</p> : null}
          {/* La soirée d'une association (une soirée Shotgun « BDE PHARMA », un événement Pretix) : qui l'organise. */}
          {!assoc && s && assos?.par_evenement[s.id] ? (
            <p className="lc-hint so-head__asso">{prefixeSoireeDe(assos.par_evenement[s.id]!.name)}<NomsAssociations associations={[assos.par_evenement[s.id]!]} prefixe="" /></p>
          ) : null}
        </div>
        <div className="so-head__side">
          <div className="so-head__pills">
            {s ? <PhasePill phase={s.phase} label={s.phase_label} /> : null}
            {/* La pastille mène à l'association (20/10/2026) ; plusieurs (09/09, INTERSANTE) : la liste courte à côté. */}
            {assoc && assosNuit.length === 1 ? (
              <Link href={`/exploitation/associations/${assosNuit[0]!.id}`} className="lc-pill so-pill-asso so-pill-asso--lien"
                title={`Ouvrir la fiche ${deNom(assosNuit[0]!.name)}`}>{assoc.libelle} · {assosNuit[0]!.name}</Link>
            ) : assoc ? <span className="lc-pill so-pill-asso">{assoc.libelle}</span> : null}
            {assoc && assosNuit.length > 1 ? <span className="lc-hint"><NomsAssociations associations={assosNuit} prefixe="" /></span> : null}
            {s ? <span className="lc-hint">Billetterie : {SOURCE_LABELS[s.source] ?? s.source}</span> : null}
          </div>
          {/* Barre d'actions de page — le bouton Modifier appartient au shell
              de la page, pas au milieu des cartes. */}
          <div className="so-actionbar">
            <Link href={`/soirees/compare?a=${id}`} className="lc-btn">
              <ArrowLeftRight size={14} aria-hidden="true" /> Comparer
            </Link>
            {/* En un clic (24/10/2026) : la précédente comparable, puis le CSV
                fabriqué par le serveur (billets, tarifs, canaux, sa nuit). */}
            <ComparerPrecedente id={id} />
            <BoutonExportCsv url={urlExportCsvSoiree(id)} secours="soiree.csv" disabled={!s}
              title="Billets de la soirée par tarif et par canal, et sa nuit, pour Excel" />
            <LayoutEditControls state={layout} />
          </div>
        </div>
      </header>

      {/* RATTACHEMENT (19/10/2026) : sur une soirée du club, ses billetteries
          (Shotgun + événements d'associations rattachés, « Défaire ») ; sur un
          événement Pretix, le bandeau qui renvoie vers sa soirée — ou la
          proposition de Gestion. Hors de la grille : ce n'est pas une tuile
          qu'on masque, c'est ce que la page EST. */}
      {/* « Soirée association » : ce qui a disparu de la page, et pourquoi — avant les tuiles. */}
      {assoc ? <p className="so-asso-note" role="note"><strong>{assoc.libelle}.</strong> {assoc.raison}</p> : null}
      {s ? (
        <BilletteriesDeLaSoiree r={detail?.rattachement} soiree={s} onChange={reloadDetail}
          venue={detail?.billetteries?.show_no_show === false ? null : detail?.formules ?? null}
          associations={assos?.par_evenement ?? null} />
      ) : null}

      {/* La phase vient du SERVEUR (nuit commerciale, coupure 08:00). La
          visibilité de chaque tuile est désormais une propriété réglable,
          plus une liste codée en dur dans le frontend. */}
      {layout.loaded ? (
        <CaisseSoireeContexte.Provider value={{ r: caisse, soireeId: id }}>
          <LayoutGrid pageKey="soiree-detail" state={layout} phase={s?.phase ?? null} absentes={absentes} />
        </CaisseSoireeContexte.Provider>
      ) : null}
      <DrillDrawers />
    </>
  )
}

export function SoireePage({ id }: { id: string }) {
  const meta = useShellMeta()
  const [inconnu, setInconnu] = useState(false)
  // Le montage réanime l'entrée (retour depuis l'index : transition douce).
  const [entered, setEntered] = useState(false)
  useEffect(() => { const t = requestAnimationFrame(() => setEntered(true)); return () => cancelAnimationFrame(t) }, [])

  if (inconnu) {
    return (
      <Shell active="soirees" meta={meta}>
        <main className="lc-page lc-stagger hub--soirees">
          <SmartBack fallback="/soirees" />
          <h1 className="lc-title">Soirée introuvable</h1>
          <p className="lc-subtitle">Cette soirée n'existe pas ou n'est plus suivie.</p>
        </main>
      </Shell>
    )
  }

  return (
    <Shell active="soirees" meta={meta}>
      <main className={`lc-page hub--soirees so-page so-route ${entered ? 'so-route--in' : ''}`}>
        <SmartBack fallback="/soirees" />
        <SoireeDataProvider id={id} onNotFound={() => setInconnu(true)}>
          <SoireeContent id={id} />
        </SoireeDataProvider>
      </main>
    </Shell>
  )
}
